import { Component, ErrorInfo, ReactNode } from 'react';
import { exportBudgetToCSV } from '../utils/exportUtils';
import { getAllBudgets } from '../db';
import { captureException } from '../utils/sentry';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to Sentry using our centralized utility
    captureException(error, {
      react: {
        componentStack: errorInfo.componentStack,
      },
    });

    // Also log to console for development
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    this.setState({
      errorInfo,
    });
  }

  handleExportData = (): void => {
    try {
      const budgets = getAllBudgets();
      if (budgets.length > 0) {
        // Export the most recent budget as a backup
        const mostRecentBudget = budgets.reduce((latest, current) => {
          const latestDate = new Date(latest.date);
          const currentDate = new Date(current.date);
          return currentDate > latestDate ? current : latest;
        });

        exportBudgetToCSV(mostRecentBudget);
      } else {
        alert('No budgets found to export.');
      }
    } catch (error) {
      console.error('Failed to export data:', error);
      alert('Failed to export data. See console for details.');
    }
  };

  handleResetApp = (): void => {
    if (window.confirm('Are you sure you want to reset the application? All data will be lost.')) {
      try {
        localStorage.removeItem('budget_app_data');
        localStorage.removeItem('budget_app_data_last_update');
        // Reload the page to start fresh
        window.location.reload();
      } catch (error) {
        console.error('Failed to reset application:', error);
        alert('Failed to reset application. See console for details.');
      }
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Render fallback UI
      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 p-4 text-center dark:bg-gray-900">
          <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-lg dark:bg-gray-800">
            <h2 className="mb-4 text-2xl font-bold text-red-600 dark:text-red-400">
              Something went wrong
            </h2>

            <p className="mb-6 text-gray-700 dark:text-gray-300">
              The application encountered an error. This might be due to corrupted data or an
              incompatible data format.
            </p>

            <div className="mb-6 rounded bg-gray-100 p-4 text-left dark:bg-gray-700">
              <p className="mb-2 font-mono text-sm text-gray-800 dark:text-gray-200">
                {this.state.error?.toString()}
              </p>
              {this.state.errorInfo && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-sm text-blue-600 dark:text-blue-400">
                    View technical details
                  </summary>
                  <pre className="mt-2 max-h-60 overflow-auto whitespace-pre-wrap break-words rounded bg-gray-200 p-2 text-xs text-gray-800 dark:bg-gray-600 dark:text-gray-200">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>

            <div className="flex flex-col space-y-4">
              <button
                onClick={this.handleExportData}
                className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:bg-blue-500 dark:hover:bg-blue-600"
              >
                Export Data Backup
              </button>

              <button
                onClick={this.handleResetApp}
                className="rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:bg-red-500 dark:hover:bg-red-600"
              >
                Reset Application
              </button>

              <button
                onClick={() => window.location.reload()}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
